import { useState, useEffect, useRef, useCallback } from "react";
import { useCookieConsent } from "@/hooks/useCookieConsent";

function Toggle({
  checked,
  onChange,
  disabled,
  label,
  description,
  badge,
}: {
  checked: boolean;
  onChange?: (v: boolean) => void;
  disabled?: boolean;
  label: string;
  description: string;
  badge?: string;
}) {
  return (
    <div className="flex items-start justify-between gap-4 py-4 border-b border-brand-border last:border-0">
      <div className="flex-1">
        <div className="flex items-center gap-2 mb-1">
          <span className="font-geist font-semibold text-brand-text">{label}</span>
          {badge && (
            <span className="inline-block px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-500">
              {badge}
            </span>
          )}
        </div>
        <p className="font-geist text-sm text-brand-text/60">{description}</p>
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        disabled={disabled}
        onClick={() => onChange && !disabled && onChange(!checked)}
        className={`relative inline-flex h-7 w-12 shrink-0 cursor-pointer rounded-full transition-colors duration-200 ${
          disabled
            ? "bg-gray-200 cursor-not-allowed"
            : checked
            ? "bg-brand-yellow"
            : "bg-gray-300 hover:bg-gray-400"
        }`}
      >
        <span
          className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition duration-200 ${
            checked ? "translate-x-6" : "translate-x-1"
          } mt-1`}
        />
      </button>
    </div>
  );
}

export default function CookiePreferencesModal() {
  const { showModal, closeModal, consent, savePreferences } = useCookieConsent();
  const [analytics, setAnalytics] = useState(consent?.analytics ?? false);
  const [marketing, setMarketing] = useState(consent?.marketing ?? false);
  const modalRef = useRef<HTMLDivElement>(null);
  const firstFocusableRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (showModal) {
      setAnalytics(consent?.analytics ?? false);
      setMarketing(consent?.marketing ?? false);
    }
  }, [showModal, consent]);

  // ESC closes modal
  useEffect(() => {
    if (!showModal) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") closeModal();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [showModal, closeModal]);

  // Focus trap + initial focus
  useEffect(() => {
    if (!showModal) return;
    const modal = modalRef.current;
    if (!modal) return;

    // Focus first focusable element
    firstFocusableRef.current?.focus();

    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Tab") return;
      const focusables = modal.querySelectorAll<HTMLElement>(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      if (focusables.length === 0) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [showModal]);

  const handleSave = useCallback(() => {
    savePreferences({ analytics, marketing });
  }, [analytics, marketing, savePreferences]);

  if (!showModal) return null;

  return (
    <div
      className="fixed inset-0 z-[10000] flex items-center justify-center bg-brand-text/80 px-4"
      onClick={closeModal}
      role="dialog"
      aria-modal="true"
      aria-label="Cookievoorkeuren"
    >
      <div
        ref={modalRef}
        className="bg-white rounded-xl max-w-[540px] w-full p-6 md:p-8 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <h2 className="font-geist font-bold text-xl md:text-2xl text-brand-text">
            Cookievoorkeuren
          </h2>
          <button
            ref={firstFocusableRef}
            type="button"
            onClick={closeModal}
            className="w-9 h-9 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors cursor-pointer"
            aria-label="Sluit cookievoorkeuren"
          >
            <i className="ri-close-line w-5 h-5 flex items-center justify-center text-xl text-brand-text/70"></i>
          </button>
        </div>

        {/* Toggle sections */}
        <div className="flex flex-col">
          <Toggle
            checked={true}
            disabled={true}
            label="Functioneel"
            badge="Altijd actief"
            description="Noodzakelijk voor de werking van de website, zoals sessies, taalvoorkeur en beveiliging."
          />
          <Toggle
            checked={analytics}
            onChange={setAnalytics}
            label="Analytisch"
            description="Helpt ons de website te verbeteren via geanonimiseerde statistieken. Bijvoorbeeld: welke pagina's worden het meest bezocht."
          />
          <Toggle
            checked={marketing}
            onChange={setMarketing}
            label="Marketing"
            description="Voor gepersonaliseerde advertenties en retargeting. Hiermee tonen wij u relevante advertenties op andere websites."
          />
        </div>

        {/* Footer buttons */}
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-end gap-3 mt-8 pt-6 border-t border-brand-border">
          <button
            type="button"
            onClick={closeModal}
            className="font-geist text-sm text-brand-text/60 underline underline-offset-2 hover:text-brand-text transition-colors cursor-pointer whitespace-nowrap"
          >
            Annuleren
          </button>
          <button
            type="button"
            onClick={handleSave}
            className="bg-brand-yellow text-brand-text font-geist font-semibold text-sm px-6 py-3 rounded-md hover:bg-brand-yellow/90 transition-colors cursor-pointer whitespace-nowrap"
          >
            Voorkeuren opslaan
          </button>
        </div>
      </div>
    </div>
  );
}